import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';
import api from '../../redux/features/auth/axiosAuthHelper';
import AuditDetailCard from '../../components/common/cards/AuditDetailCard';
import RecordDetailDialog from '../../components/common/RecordDetailDialog';
import AuditTable from '../../components/common/tables/AuditTable';
import { useAuditTable } from '../useInventoryTables';

const AuditsPage = () => {
  const authData = useSelector((state: RootState) => state.auth);
  const table = useAuditTable(authData.isAuthenticated, authData.access);
  const [audit, setAudit] = useState<any>(null);
  const [popup, setPopup] = useState('');
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const { paramID } = useParams<{ paramID: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const openedFromTable = useRef<string | null>(null);
  useEffect(() => {
    if (location.state?.fromTable) {
      openedFromTable.current = paramID ?? null;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (paramID && openedFromTable.current === paramID) {
      openedFromTable.current = null;
      return;
    }
    openedFromTable.current = null;
    if (paramID?.toLowerCase() === 'create') {
      navigate('/auditlog', { replace: true });
    } else if (paramID && /^\d+$/.test(paramID)) {
      let active = true;
      api.get(`audit-logs/${paramID}/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      }).then(response => {
        if (!active) return;
        setAudit(response.data);
        setPopup('Edit');
      }).catch(error => {
        if (!active) return;
        setAudit({ id: paramID });
        setPopup('Edit');
        console.error('Error fetching audit:', error);
      });
      return () => { active = false; };
    } else if (paramID) {
      navigate('/auditlog', { replace: true });
    }
  }, [paramID, location.pathname, location.state, navigate, authData.access]);

  const handleRefreshButton = () => {
    setAudit(null);
    setPopup('');
    navigate('/auditlog', { replace: true });
    table.refresh();
  };

  const handleBackButton = () => {
    setAudit(null);
    setPopup('');
    navigate('/auditlog', { replace: true });
  };

  const openEditPage = (row: any) => {
    setAudit(row.original);
    setPopup('Edit');
    navigate(`/auditlog/${row.original.id}`, { replace: true, state: { fromTable: true } });
  };

  return authData.isAuthenticated === false ? (
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : authData.isAuthenticated ? (
    <div data-testid="div-9bus" style={{ overflowX: "hidden", maxHeight: `100%`, minHeight: `100%`, maxWidth: `100%`, minWidth: `100%`, margin: "auto" }}>

      <Box sx={{ p: { xs: 1, md: 1.5 }, height: '100dvh', minHeight: 360, boxSizing: 'border-box', minWidth: 0 }}>
                    <AuditTable
                      rows={table.rows}
                      rowCount={table.rowCount}
                      isLoading={table.isLoading}
                      hasLoaded={table.hasLoaded}
                      error={table.error}
                      refresh={table.refresh}
                      sorting={table.sorting}
                      columnVisibility={columnVisibility}
                      columnFilters={table.columnFilters}
                      pagination={table.pagination}
                      globalFilter={table.globalFilter}
                      rowSelection={table.rowSelection}
                      setRowSelection={table.setRowSelection}
                      showGlobalFilter={showGlobalFilter}
                      setShowGlobalFilter={setShowGlobalFilter}
                      handleRemoveAllFilters={table.clearFilters}
                      setGlobalFilter={table.onGlobalFilterChange}
                      setSorting={table.onSortingChange}
                      setColumnVisibility={setColumnVisibility}
                      setColumnFilters={table.onColumnFiltersChange}
                      setPagination={table.onPaginationChange}
                      handleRefreshButton={handleRefreshButton}
                      openEditPage={openEditPage}
                    />
      </Box>
      <RecordDetailDialog open={!!audit && popup === 'Edit'} title="View Audit Log" onClose={handleBackButton} compactHeader>
        {audit && <AuditDetailCard audit_id={audit.id} handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} />}
      </RecordDetailDialog>
    </div>
  ) : null;
};

export default AuditsPage;
